import React, { memo, useCallback, useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { RichText } from '@Sage/ssm-web5';
import { Popover, Icon, Advice, useTheme, Text, Spacer, Markdown } from '@Sage/hoi-poi-ui';
import { FuzzyMap } from 'utils/fuzzy';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import InsertFieldTool from './InsertFieldTool';
import LanguageSelector from './LanguageSelector';
import TimezoneSelector from './TimezoneSelector';

import './styles.scss';

const InputTemplated = memo(
    ({
        insertFieldOptions,
        value,
        onChange,
        onChangeLocale,
        onChangeTimeZone,
        locale,
        timeZone,
        placeholder,
        error,
        isNumber,
    }) => {
        const theme = useTheme();
        const rootRef = useRef(null);
        const [isFocused, setIsFocused] = useState(false);
        const editor = useRef(null);
        const insertFieldMenuRef = useRef(null);
        const [withLanguageSelector, setWithLanguageSelector] = useState(false);
        const [withTimezoneSelector, setWithTimezoneSelector] = useState(false);

        const initialValue = useRef(value);
        const isInitialized = useRef(false);

        const handleChange = useCallback(
            (v) => {
                // Skip onChange on first update if value hasn't actually changed
                if (!isInitialized.current && v === initialValue.current) {
                    isInitialized.current = true;
                    return;
                }
                isInitialized.current = true;
                onChange && onChange(v);
            },
            [onChange],
        );

        const getEditorRef = useCallback((ref) => {
            editor.current = ref;
        }, []);

        const onInsertField = useCallback((value) => {
            setIsFocused(true);
            editor.current
                .chain()
                .insertVariable(value?.value?.replace(/\{\{\s*([^}]+?)\s*\}\}/g, '$1'))
                .focus()
                .run();
        }, []);

        const popoverProps = useMemo(
            () => ({
                content: isNumber ? (
                    <div className="fm-automation__input-templated__info-popover">
                        <Text type="subtitle" medium>
                            {getLiteral('label_formula_rules')}
                        </Text>
                        <Spacer y={3} />
                        <Text>{getLiteral('label_formula_rules_desc')}</Text>
                        <Spacer y={1} />
                        <Text>
                            {getLiteralWithParameters('label_formula_rules_desc_example', [
                                '{{ .event.data.total }}',
                                '{{ .event.data.tax }}',
                            ])}
                        </Text>
                        <Spacer y={1} />
                        <Markdown
                            content={getLiteral('label_formula_rules_desc_2')}
                            overrides={{ a: { target: '_blank' } }}
                        />
                    </div>
                ) : undefined,
                overlayStyle: { zIndex: 9999999 },
            }),
            [isNumber],
        );

        const variables = useMemo(
            () =>
                insertFieldOptions?.options?.reduce((obj, item) => {
                    item.options.forEach((option) => {
                        const key = option.value.replace(/\{\{\s*([^}]+?)\s*\}\}/g, '$1');
                        const parentFilter = insertFieldOptions.filterOptions.find((filter) =>
                            key.includes(filter.value),
                        );
                        obj[key] = {
                            ...option,
                            label: `${option.label} (${parentFilter.label})`,
                            icon: parentFilter.iconType,
                        };
                    });
                    return obj;
                }, {}),
            [insertFieldOptions],
        );

        const convertValueToHtml = useCallback((text) => {
            if (!text) return text;
            return text.replace(
                /\{\{\s*([^}]+?)\s*\}\}/g,
                (match, key) => `<span data-variable="${key.trim()}">${match}</span>`,
            );
        }, []);

        const checkVariableType = useCallback(
            (key, expectedType) => {
                if (
                    !variables ||
                    !variables[key] ||
                    FuzzyMap[variables[key].table?.table?.toLowerCase()]
                ) {
                    return false;
                }

                const type = variables[key]?.type;
                return expectedType
                    ? Array.isArray(expectedType)
                        ? expectedType.includes(type)
                        : type === expectedType
                    : false;
            },
            [variables],
        );

        const checkToolbarVariables = useCallback(
            (text) => {
                const matches = text.match(/\{\{\s*([^}]+?)\s*\}\}/g);
                if (matches) {
                    // Extract the variable keys without the braces
                    const extractedKeys = matches.map((match) =>
                        match.replace(/\{\{\s*([^}]+?)\s*\}\}/g, '$1').trim(),
                    );

                    // Verify if the keys exist in the variables object and check their types
                    if (variables && Object.keys(variables).length > 0) {
                        const needLanguageSelector = !!extractedKeys.find((key) =>
                            checkVariableType(key, ['list', 'multivalue']),
                        );

                        const needTimezoneSelector = !!extractedKeys.find((key) =>
                            checkVariableType(key, ['datetime']),
                        );

                        setWithLanguageSelector(needLanguageSelector);
                        setWithTimezoneSelector(needTimezoneSelector);
                    }
                } else {
                    setWithLanguageSelector(false);
                    setWithTimezoneSelector(false);
                }
            },
            [variables, checkVariableType],
        );

        const htmlValue = useMemo(() => convertValueToHtml(value), [value, convertValueToHtml]);

        const editorProps = useMemo(
            () => ({
                value: htmlValue,
                hideClear: true,
                isFullWidth: true,
                withCustomToolbar: true,
                className: 'fm-automation__input-templated__input',
                placeholder,
                onCreate: (value) => {
                    setTimeout(() => {
                        checkToolbarVariables(value.text);
                    });
                },
                onChange: (value) => {
                    handleChange(value.text);
                    setTimeout(() => {
                        checkToolbarVariables(value.text);
                    });
                },
                getEditorRef,
                overrides: {
                    editor: { style: { height: '100%' } },
                    editorWrapper: {
                        style: {
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            borderRadius: '0 0 8px 8px',
                            height: '100%',
                        },
                    },
                },
            }),
            [htmlValue, placeholder, getEditorRef, handleChange, checkToolbarVariables],
        );

        const onInnerChangeLocale = useCallback(
            (v) => {
                setIsFocused(true);
                onChangeLocale && onChangeLocale(v);
            },
            [onChangeLocale],
        );

        const onInnerChangeTimeZone = useCallback(
            (v) => {
                setIsFocused(true);
                onChangeTimeZone && onChangeTimeZone(v);
            },
            [onChangeTimeZone],
        );

        useEffect(() => {
            const handleRootFocus = (e) => {
                if (
                    rootRef?.current?.contains(e.target) ||
                    insertFieldMenuRef.current?.contains(e.target)
                ) {
                    setIsFocused(true);
                } else {
                    setIsFocused(false);
                }
            };

            document.addEventListener('mousedown', handleRootFocus);
            return () => {
                document.removeEventListener('mousedown', handleRootFocus);
            };
        }, []);

        const hasVariables = useMemo(() => {
            return Object.keys(variables).length > 0;
        }, [variables]);

        return (
            <div
                ref={rootRef}
                className={classnames('fm-automation__input-templated', {
                    'fm-automation__input-templated--focused': isFocused,
                })}
            >
                <div className="fm-automation__input-templated__toolbar">
                    <InsertFieldTool
                        insertFieldOptions={insertFieldOptions}
                        onChange={onInsertField}
                        ref={insertFieldMenuRef}
                    />
                    {withLanguageSelector && (
                        <LanguageSelector onChange={onInnerChangeLocale} locale={locale} />
                    )}
                    {withTimezoneSelector && (
                        <TimezoneSelector onChange={onInnerChangeTimeZone} timeZone={timeZone} />
                    )}
                    {isNumber && (
                        <Popover {...popoverProps}>
                            <Icon
                                name="info"
                                color={theme.colors.actionMinor[500]}
                                className="fm-automation__input-templated__info-icon"
                            />
                        </Popover>
                    )}
                </div>
                {hasVariables && <RichText {...editorProps} variables={variables} />}
                {error && (
                    <Advice
                        className="fm-automation__input-templated__error"
                        type="error"
                        defaultCollapsed={false}
                        showIcon
                    >
                        {error}
                    </Advice>
                )}
            </div>
        );
    },
);

InputTemplated.displayName = 'InputTemplated';

export default InputTemplated;
